import { API_BASE_URL } from '../constants/config';
import type { IntakeTestParams, PublicIntakeDraft, PublicIntakeView } from '../types/intake';

/**
 * PUBLIC intake API — raw fetch, no auth interceptor (the token IS the
 * capability; the page renders outside the authenticated shell).
 */
const BASE = `${API_BASE_URL}/public/intake`;

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = 'Request failed.';
    try {
      const body = await res.json();
      message = body?.error || body?.message || message;
    } catch { /* non-JSON error body */ }
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}

/** General-portal start: mints a fresh draft and returns its token once. */
export const startPublicIntake = async (): Promise<{ token: string }> =>
  handle<{ token: string }>(await fetch(`${BASE}/start`, { method: 'POST' }));

export const getPublicIntake = async (token: string): Promise<PublicIntakeView> =>
  handle<PublicIntakeView>(await fetch(`${BASE}/${token}`));

export const savePublicIntakeDraft = async (
  token: string,
  draft: PublicIntakeDraft,
): Promise<{ savedAt: string }> =>
  handle<{ savedAt: string }>(await fetch(`${BASE}/${token}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  }));

/** Pull a Submitted request back for edits (before the lab processes it). */
export const reopenPublicIntake = async (token: string): Promise<{ ok: boolean }> =>
  handle<{ ok: boolean }>(await fetch(`${BASE}/${token}/reopen`, { method: 'POST' }));

/** Clone a PROCESSED request into a fresh editable draft (new token). */
export const revisePublicIntake = async (token: string): Promise<{ token: string }> =>
  handle<{ token: string }>(await fetch(`${BASE}/${token}/revise`, { method: 'POST' }));

/** The offline Excel version of this request (prefilled with the draft). */
export const intakeWorkbookUrl = (token: string): string => `${BASE}/${token}/workbook`;

/** Import a filled workbook — replaces the draft's fields + sample grid. */
export const importIntakeWorkbook = async (
  token: string,
  file: File,
): Promise<{ warnings: string[]; sampleCount: number }> => {
  const form = new FormData();
  form.append('file', file);
  return handle<{ warnings: string[]; sampleCount: number }>(
    await fetch(`${BASE}/${token}/import`, { method: 'POST', body: form }),
  );
};

// ── Import-with-confirmation (ours OR legacy KeyLAB) ─────────────────────────

export interface ImportColumn {
  columnId: string;
  sheet: string;
  label: string;
  astm: string | null;
  resolvedTestKey: string | null;
  resolvedTestName: string | null;
  method: 'astm' | 'name' | 'unresolved';
  candidates: { key: string; name: string; astm: string | null }[];
  dataCount: number;
}

export interface ImportPreviewSample {
  locationId: string | null;
  topDepth: number | null;
  baseDepth: number | null;
  sampleRef: string | null;
  sampleType: string | null;
  tests: { testKey: string; quantity: number; params: IntakeTestParams | null }[];
  unresolved: { columnId: string; quantity: number; stressesKpa?: number[]; paramNote?: string }[];
  entries: { columnId: string; quantity: number; stressesKpa?: number[]; paramNote?: string }[];
}

export interface ImportPreview {
  source: 'moftaki' | 'keylab';
  fields: Partial<PublicIntakeDraft>;
  samples: ImportPreviewSample[];
  columns: ImportColumn[];
  warnings: string[];
}

/** Parse a workbook WITHOUT saving — the confirm dialog applies the result. */
export const analyzeIntakeImport = async (token: string, file: File): Promise<ImportPreview> => {
  const form = new FormData();
  form.append('file', file);
  return handle<ImportPreview>(
    await fetch(`${BASE}/${token}/analyze-import`, { method: 'POST', body: form }),
  );
};

export const submitPublicIntake = async (
  token: string,
  draft: PublicIntakeDraft,
): Promise<{ submittedAt: string; warnings: string[] }> =>
  handle<{ submittedAt: string; warnings: string[] }>(await fetch(`${BASE}/${token}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  }));
